import 'react-calendar/dist/Calendar.css';
import './rangeCalendar.scss';

import dayjs, { Dayjs } from 'dayjs';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Calendar as ReactCalendar, CalendarTileProperties } from 'react-calendar';
import TimePickerDefault, { TimePickerValue } from 'react-time-picker';

import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';

const TimePicker = (TimePickerDefault as any).default || TimePickerDefault;

type RangeCalendarProps = {
  onSubmit?: () => void;
  onClose: () => void;
  title: string;
  handleDefaultTotal?: () => void;
  handleFilter: ([date1, date2]: [Dayjs | null, Dayjs | null]) => void;
  initialStartDate: Dayjs | null;
  initialEndDate: Dayjs | null;
  withTime?: boolean;
};

export const RangeCalendar = ({
  onSubmit,
  onClose,
  title,
  handleDefaultTotal,
  handleFilter,
  initialStartDate,
  initialEndDate,
  withTime = false,
}: RangeCalendarProps) => {
  const calendarModalRef = useRef<HTMLDivElement>(null);

  const [startDate, setStartDate] = useState<Dayjs | null>(
    initialStartDate ? initialStartDate.utc() : dayjs.utc().startOf('day'),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    initialEndDate ? initialEndDate.utc() : dayjs.utc(),
  );

  const onHandleFilter = useCallback(() => {
    onClose();
    onSubmit?.();
    handleFilter([startDate, endDate]);
  }, [onClose, onSubmit, handleFilter, startDate, endDate]);

  const onHandleDefaultTotal = useCallback(() => {
    onClose();
    if (handleDefaultTotal) {
      handleDefaultTotal();
    }
  }, [onClose, handleDefaultTotal]);

  // Note: We intentionally don't sync with initialStartDate/initialEndDate after mount.
  // The calendar should maintain its own selection state while open, and only
  // apply changes when the user clicks Submit. Syncing with props caused a bug
  // where selecting a new date range would revert to the previous selection
  // due to debounced updates from the parent component.

  const keyDownEvent = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      event.stopPropagation();

      if (event.key === 'Escape' || event.key === 'Enter') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (calendarModalRef.current) {
      calendarModalRef.current.focus();
    }
  }, [calendarModalRef]);

  const onChangeTime = useCallback(
    (value: TimePickerValue, type: 'start' | 'end') => {
      if (!value) return;

      const hours = Number(value.toString().split(':')[0]);
      const minutes = Number(value.toString().split(':')[1]);

      const date = type === 'start' ? startDate : endDate;
      if (!date) return;

      const newDateResult = date
        .utc()
        .set('hour', hours)
        .set('minute', minutes)
        .set('second', 0)
        .set('millisecond', 0);

      if (type === 'start') {
        setStartDate(newDateResult);
      } else {
        setEndDate(newDateResult);
      }
    },
    [endDate, startDate],
  );

  const timeFromDate = useCallback((date: Dayjs | null) => {
    if (date === null) return '00:00';

    return date.utc().format('HH:mm');
  }, []);

  const handleChangeCalendar = (values: [Date] | [Date, Date]) => {
    const todayLocalStr = dayjs().format('YYYY-MM-DD');

    const startLocalStr = dayjs(values[0]).format('YYYY-MM-DD');
    const endLocalStr = values[1] ? dayjs(values[1]).format('YYYY-MM-DD') : startLocalStr;

    const startUtc = dayjs.utc(startLocalStr, 'YYYY-MM-DD');
    const endUtc = dayjs.utc(endLocalStr, 'YYYY-MM-DD');

    // Set start date
    if (startLocalStr === todayLocalStr) {
      setStartDate(dayjs.utc(todayLocalStr, 'YYYY-MM-DD'));
    } else {
      setStartDate(startUtc);
    }

    // Set end date
    if (endLocalStr === todayLocalStr) {
      // If today, use current UTC time
      setEndDate(dayjs.utc());
    } else {
      // If not today, set to end of day (23:59:59)
      setEndDate(
        endUtc.set('hour', 23).set('minute', 59).set('second', 59).set('millisecond', 999),
      );
    }
  };

  const isTileDisabled = ({ date, view }: CalendarTileProperties): boolean => {
    if (view === 'month') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return date > today;
    }

    return false;
  };

  return (
    <Modal onClose={onClose} title={title} backdropClassName="mm-range-calendar-modal-backdrop">
      <div
        className="mm-range-calendar-modal"
        ref={calendarModalRef}
        tabIndex={0}
        onKeyDown={keyDownEvent}
      >
        <ReactCalendar
          locale="en"
          selectRange
          value={[
            startDate ? dayjs(startDate.utc().format('YYYY-MM-DD')).toDate() : null,
            endDate ? dayjs(endDate.utc().format('YYYY-MM-DD')).toDate() : null,
          ]}
          tileDisabled={isTileDisabled}
          onChange={handleChangeCalendar}
        />

        <div className="mm-range-calendar-modal__selected-dates">
          <div className="selected-date-container">
            <div className="selected-date-side">
              <span className="date-caption">Start date:</span>
              <span>{startDate ? startDate.utc().format('L') : ''}</span>
            </div>

            {withTime && (
              <>
                <div className="date-divider" />
                <div className="selected-date-side">
                  <span className="date-caption">Time UTC+0:</span>
                  {startDate && (
                    <TimePicker
                      className="date-time-picker"
                      onChange={(value: TimePickerValue) => onChangeTime(value, 'start')}
                      value={timeFromDate(startDate)}
                      format="HH:mm"
                      clockIcon={null}
                      clearIcon={null}
                      disableClock
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="selected-date-container">
            <div className="selected-date-side">
              <span className="date-caption">End date:</span>
              <span>{endDate ? endDate.utc().format('L') : ''}</span>
            </div>

            {withTime && (
              <>
                <div className="date-divider" />
                <div className="selected-date-side">
                  <span className="date-caption">Time UTC+0:</span>
                  {endDate && (
                    <TimePicker
                      className="date-time-picker"
                      onChange={(value: TimePickerValue) => onChangeTime(value, 'end')}
                      value={timeFromDate(endDate)}
                      format="HH:mm"
                      clockIcon={null}
                      clearIcon={null}
                      disableClock
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mm-range-calendar-modal__buttons">
          <Button color="primary" onClick={onHandleFilter}>
            Submit
          </Button>

          {handleDefaultTotal && (
            <Button color="secondary" onClick={onHandleDefaultTotal}>
              Default (Total)
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
